import React from "react";
import {
  ScanSearch,
  ScanText,
  Atom,
  Table2,
  BookText,
  Search as SearchIcon,
  Workflow,
  LucideIcon,
  Sparkles,
  MoveRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { ProcessCard } from "./components/ProcessCard";

interface ProcessStep {
  icon: LucideIcon;
  number: number;
  title: string;
  description: string;
}

const HowItWorks: React.FC = () => {
  const processSteps: ProcessStep[] = [
    {
      icon: ScanSearch,
      number: 1,
      title: "Intelligent Extraction",
      description:
        "Our AI algorithms scan and identify areas of interest in your documents, handling diverse layouts from physical books to digitised PDFs. The system automatically identifies tables, chemical structures, text blocks, and other critical elements.",
    },
    {
      icon: ScanText,
      number: 2,
      title: "Advanced Character Recognition",
      description:
        "State-of-the-art OCR models accurately transcribe everything from printed text to difficult handwritten notes. Our models are specifically trained on scientific documents to handle specialized terminology and notation.",
    },
    {
      icon: Atom,
      number: 3,
      title: "Chemical Structure Recognition",
      description:
        "Specialised OCSR models interpret molecular diagrams using various notations and drawing styles. This unlocks the vast amounts of information trapped within chemical structure diagrams.",
    },
    {
      icon: Table2,
      number: 4,
      title: "Table Structuring",
      description:
        "Innovative techniques detect and structure tables, preserving the relationships between data points. Tables are some of the most information-dense elements in scientific documents, and we ensure nothing is lost.",
    },
    {
      icon: BookText,
      number: 5,
      title: "Document Structuring",
      description:
        "Information is brought together across all pages to provide a cohesive understanding of each document. Context is preserved and relationships between different sections are maintained.",
    },
    {
      icon: SearchIcon,
      number: 6,
      title: "Intelligent Search",
      description:
        "Powerful search algorithms make your unique datasets fully searchable. Perform text searches, chemical structure searches, and semantic queries across millions of records to find exactly what you need.",
    },
    {
      icon: Workflow,
      number: 7,
      title: "Seamless Integration",
      description:
        "Export structured data to electronic lab notebooks and other research tools. Our standardised formats ensure compatibility with the workflows you're already using.",
    },
  ];

  return (
    <div className="bg-dark-bg min-h-screen">
      <div className="container mx-auto w-full max-w-4xl px-5 md:px-0 text-center">
        <h1 className="text-5xl md:text-[3.5rem] leading-none font-semibold pt-[10%]">
          <span className="bg-gradient-text bg-clip-text text-transparent">
            How We Revive <br /> Your Chemical Data
          </span>
        </h1>

        <p className="mt-6 text-white text-lg max-w-3xl mx-auto leading-relaxed">
          Data Revival transforms locked chemical archives into searchable,
          structured data through our AI-powered pipeline. From document
          scanning to intelligent search, we've built a comprehensive suite of
          tools designed specifically for chemical data extraction and analysis.
        </p>
      </div>

      {/* cards */}
      <section className="mt-20 pb-16">
        <div className="container mx-auto w-full max-w-4xl px-5 md:px-0">
          <div className="text-center mb-12">
            <h2 className="font-bold text-white text-3xl md:text-4xl">
              The Complete Process
            </h2>
          </div>

          <div className="space-y-0">
            {processSteps.map((step, index) => (
              <ProcessCard
                key={step.number}
                icon={step.icon}
                number={step.number}
                title={step.title}
                description={step.description}
                isLast={index === processSteps.length - 1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pb-16">
        <div className="container mx-auto w-full max-w-4xl px-5 md:px-0">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 md:p-12 text-white text-center transition-all duration-300 hover:border-white/30">
            <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-[#00A3FF] to-[#0077CC] shadow-lg shadow-[#00A3FF]/20">
              <Sparkles size={32} className="text-white" />
            </div>
            <h3 className="font-bold text-3xl md:text-4xl mt-4 mb-6">
              Ready to revive your data?
            </h3>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
              Join organisations worldwide who are unlocking the hidden potential
              in their chemical archives. Transform decades of locked data into
              actionable insights with our AI-powered platform.
            </p>
            <Link
              to="/pricingPlans"
              className="inline-flex items-center justify-center bg-gradient-to-r from-[#00A3FF] to-[#0077CC] hover:shadow-lg hover:shadow-[#00A3FF]/30 rounded-lg px-6 py-3 text-white font-semibold transition-all duration-200"
            >
              Get Started <MoveRight className="ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
